import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MenuModule, Menu } from 'primeng/menu';

import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';

import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { PontoCriticoService } from '@core/services/domain-services';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { PontoCritico } from '@core/models/models';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import {
  PontoCriticoQrCode,
  PontoCriticoQrCodeService,
} from '@shared/api/services/ponto-critico-qrcode.service';

@Component({
  selector: 'app-posto-checkpoints-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DrawerModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    MenuModule,
    TooltipModule,
    SkeletonModule,
    PaginatorControlComponent,
  ],
  templateUrl: './posto-checkpoints-tab.component.html',
  styleUrl: './posto-checkpoints-tab.component.scss',
})
export class PostoCheckpointsTabComponent implements OnInit, OnDestroy {
  @Input() postoId!: number;
  @Output() countChange = new EventEmitter<number>();

  private readonly pontoCriticoSvc = inject(PontoCriticoService);
  private readonly msgService = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly qrCodeSvc = inject(PontoCriticoQrCodeService);

  pontosCriticos: PontoCritico[] = [];
  loading = false;
  page = 1;
  pageSize = 10;
  totalRecords = 0;

  readonly rondaPresets = [1, 2, 3, 4];

  // Drawer
  showDrawer = false;
  editingPonto: PontoCritico | null = null;
  loadingSavePonto = false;

  pontoForm!: FormGroup;
  pontoMenuItems: MenuItem[] = [];
  selectedPonto: PontoCritico | null = null;

  readonly qrCodesByPonto = new Map<number, PontoCriticoQrCode>();
  showQrDrawer = false;
  qrPonto: PontoCritico | null = null;
  activeQrCode: PontoCriticoQrCode | null = null;
  qrPreviewUrl: string | null = null;
  loadingQr = false;
  generatingQr = false;
  downloadingQr = false;
  qrPreviewUnavailable = false;

  ngOnInit(): void {
    this.pontoForm = this.fb.group({
      designacao: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],
      rondaPorTurno: [
        1,
        [Validators.required, Validators.min(1), Validators.max(20)],
      ],
      localizacao: ['', Validators.maxLength(160)],
      obrigatorio: [true],
    });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.pontoCriticoSvc
      .getAll({
        PostoId: this.postoId,
        pageNumber: this.page,
        pageSize: this.pageSize,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (res) => {
          this.pontosCriticos = res.data ?? [];
          this.totalRecords = res.totalRecords ?? this.pontosCriticos.length;
          this.countChange.emit(this.totalRecords);
          this.loadQrStatuses();
        },
        error: () => {
          this.pontosCriticos = [];
          this.totalRecords = 0;
          this.countChange.emit(0);
        },
      });
  }

  ngOnDestroy(): void {
    this.revokeQrPreview();
  }

  onPageChange(event: { page?: number; rows?: number }): void {
    const nextPageSize = event.rows ?? this.pageSize;
    this.page = nextPageSize !== this.pageSize ? 1 : (event.page ?? 0) + 1;
    this.pageSize = nextPageSize;
    this.load();
  }

  openNovoPonto(): void {
    this.editingPonto = null;
    this.pontoForm.reset({
      designacao: '',
      rondaPorTurno: 1,
      obrigatorio: true,
      localizacao: '',
    });
    this.showDrawer = true;
  }

  openEditPonto(p: PontoCritico): void {
    this.editingPonto = p;
    this.pontoForm.patchValue({
      designacao: p.designacao,
      rondaPorTurno: p.rondaPorTurno ?? 1,
      localizacao: p.localizacao ?? '',
      obrigatorio: p.obrigatorio ?? true,
    });
    this.showDrawer = true;
  }

  buildPontoMenu(p: PontoCritico, menu: Menu, event: Event): void {
    this.selectedPonto = p;
    this.pontoMenuItems = [
      {
        label: 'Editar',
        icon: 'ri-pencil-line',
        command: () => this.openEditPonto(p),
      },
      {
        label: 'Eliminar',
        icon: 'ri-delete-bin-line',
        styleClass: 'menu-item-danger',
        command: () => this.deletePontoCritico(p),
      },
    ];
    menu.toggle(event);
  }

  salvarPontoCritico(): void {
    if (this.pontoForm.invalid) {
      this.pontoForm.markAllAsTouched();
      return;
    }
    this.loadingSavePonto = true;

    const createPayload: PontoCritico = {
      idPosto: this.postoId,
      designacao: this.pontoForm.value.designacao.trim(),
      rondaPorTurno: this.pontoForm.value.rondaPorTurno,
      localizacao: this.pontoForm.value.localizacao || null,
      obrigatorio: this.pontoForm.value.obrigatorio ?? true,
    };

    const updatePayload = {
      idPosto: this.postoId,
      designacao: this.pontoForm.value.designacao.trim(),
      rondaPorTurno: this.pontoForm.value.rondaPorTurno,
      localizacao: this.pontoForm.value.localizacao || null,
      obrigatorio: this.pontoForm.value.obrigatorio ?? true,
    };

    const obs = this.editingPonto?.id
      ? this.pontoCriticoSvc.update(this.editingPonto.id, updatePayload)
      : this.pontoCriticoSvc.create(createPayload);

    obs
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loadingSavePonto = false)),
      )
      .subscribe({
        next: () => {
          this.msgService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Ponto crítico guardado',
            life: 3000,
          });
          this.closeDrawer();
          this.load();
        },
        error: () => {
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao guardar ponto crítico',
            life: 3000,
          });
        },
      });
  }

  closeDrawer(): void {
    this.showDrawer = false;
    this.loadingSavePonto = false;
    this.editingPonto = null;
  }

  deletePontoCritico(p: PontoCritico): void {
    this.confirm.confirm({
      header: 'Eliminar Checkpoint',
      message: `O checkpoint "${p.designacao}" será eliminado.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (!p.id) return;
        this.pontoCriticoSvc
          .delete(p.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msgService.add({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Ponto crítico eliminado',
                life: 3000,
              });
              if (this.pontosCriticos.length === 1 && this.page > 1) {
                this.page -= 1;
              }
              this.load();
            },
            error: () => {
              this.msgService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Não foi possível eliminar o checkpoint.',
                life: 3000,
              });
            },
          });
      },
    });
  }

  hasActiveQr(ponto: PontoCritico): boolean {
    return ponto.id != null && this.qrCodesByPonto.has(ponto.id);
  }

  openQrCode(ponto: PontoCritico): void {
    if (ponto.id == null) return;
    this.qrPonto = ponto;
    this.activeQrCode = this.qrCodesByPonto.get(ponto.id) ?? null;
    this.qrPreviewUnavailable = false;
    this.showQrDrawer = true;
    this.refreshSelectedQr();
  }

  closeQrDrawer(): void {
    this.showQrDrawer = false;
    this.qrPonto = null;
    this.activeQrCode = null;
    this.loadingQr = false;
    this.generatingQr = false;
    this.downloadingQr = false;
    this.qrPreviewUnavailable = false;
    this.revokeQrPreview();
  }

  generateQrCode(): void {
    if (!this.qrPonto?.id || this.generatingQr) return;

    if (this.activeQrCode) {
      this.confirm.confirm({
        header: 'Regenerar QR Code',
        message:
          'O QR Code atual será invalidado. As cópias já impressas deixarão de funcionar.',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Regenerar',
        rejectLabel: 'Cancelar',
        accept: () => this.executeQrGeneration(),
      });
      return;
    }

    this.executeQrGeneration();
  }

  downloadQrCode(): void {
    const ponto = this.qrPonto;
    if (!ponto?.id || !this.activeQrCode || this.downloadingQr) return;

    this.downloadingQr = true;
    this.qrCodeSvc
      .preview(ponto.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.downloadingQr = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const safeName = (ponto.designacao || 'checkpoint')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9_-]+/g, '-')
            .replace(/^-|-$/g, '')
            .toLowerCase();
          link.href = url;
          link.download = 'qr-' + (safeName || 'checkpoint') + '.png';
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
        },
        error: () => this.cdr.markForCheck(),
      });
  }

  printQrCode(): void {
    if (!this.qrPreviewUrl || !this.activeQrCode) return;

    const printWindow = window.open('', '_blank', 'width=700,height=800');
    if (!printWindow) {
      this.msgService.add({
        severity: 'warn',
        summary: 'Impressão bloqueada',
        detail: 'Autorize janelas pop-up para imprimir o QR Code.',
        life: 4000,
      });
      return;
    }

    const designacao = this.qrPonto?.designacao || 'Checkpoint';
    const localizacao = this.qrPonto?.localizacao || 'Localização não definida';
    const publicId = this.activeQrCode.publicId || '—';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Imprimir QR Code - ${designacao}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              color: #1a1a1a;
              text-align: center;
            }
            .card {
              border: 2px dashed #000;
              padding: 40px;
              border-radius: 16px;
              max-width: 400px;
              width: 100%;
              box-sizing: border-box;
            }
            .title {
              font-size: 24px;
              font-weight: 700;
              margin: 0 0 8px 0;
              text-transform: uppercase;
            }
            .subtitle {
              font-size: 14px;
              color: #555;
              margin: 0 0 24px 0;
            }
            .qr-img {
              width: 260px;
              height: 260px;
              object-fit: contain;
              margin-bottom: 24px;
            }
            .meta {
              font-size: 12px;
              color: #666;
              border-top: 1px solid #eee;
              padding-top: 16px;
            }
          </style>
        </head>
        <body>
          <div class="card">
            <h1 class="title">${designacao}</h1>
            <p class="subtitle">${localizacao}</p>
            <img class="qr-img" src="${this.qrPreviewUrl}" alt="QR Code" onload="window.print(); window.close();" />
            <div class="meta">
              <strong>ROTA DE RONDA</strong><br/>
              ID da Versão: ${publicId}
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
  }

  private loadQrStatuses(): void {
    const pointIds = this.pontosCriticos
      .map((ponto) => ponto.id)
      .filter((id): id is number => id != null);

    this.qrCodesByPonto.clear();
    if (pointIds.length === 0) return;

    forkJoin(
      pointIds.map((pontoCriticoId) =>
        this.qrCodeSvc.getActive(pontoCriticoId).pipe(
          map((page) => ({
            pontoCriticoId,
            qrCode: this.selectActiveQr(page.data),
          })),
          catchError(() => of({ pontoCriticoId, qrCode: null })),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        results.forEach(({ pontoCriticoId, qrCode }) => {
          if (qrCode) this.qrCodesByPonto.set(pontoCriticoId, qrCode);
        });
        this.cdr.markForCheck();
      });
  }

  private refreshSelectedQr(): void {
    const pontoCriticoId = this.qrPonto?.id;
    if (pontoCriticoId == null) return;

    this.loadingQr = true;
    this.revokeQrPreview();
    this.qrCodeSvc
      .getActive(pontoCriticoId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingQr = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (page) => {
          if (this.qrPonto?.id !== pontoCriticoId) return;
          const qrCode = this.selectActiveQr(page.data);
          this.activeQrCode = qrCode;
          if (qrCode) {
            this.qrCodesByPonto.set(pontoCriticoId, qrCode);
            this.loadQrPreview(pontoCriticoId);
          } else {
            this.qrCodesByPonto.delete(pontoCriticoId);
          }
        },
        error: () => {
          if (this.qrPonto?.id === pontoCriticoId) {
            this.activeQrCode = null;
            this.cdr.markForCheck();
          }
        },
      });
  }

  private loadQrPreview(pontoCriticoId: number): void {
    this.qrPreviewUnavailable = false;
    this.qrCodeSvc
      .preview(pontoCriticoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          if (this.qrPonto?.id !== pontoCriticoId) return;
          this.revokeQrPreview();
          this.qrPreviewUrl = URL.createObjectURL(blob);
          this.cdr.markForCheck();
        },
        error: () => {
          if (this.qrPonto?.id === pontoCriticoId) {
            this.qrPreviewUnavailable = true;
            this.cdr.markForCheck();
          }
        },
      });
  }

  private executeQrGeneration(): void {
    const pontoCriticoId = this.qrPonto?.id;
    if (pontoCriticoId == null) return;

    this.generatingQr = true;
    this.qrCodeSvc
      .generate(pontoCriticoId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.generatingQr = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.msgService.add({
            severity: 'success',
            summary: 'QR Code atualizado',
            detail: 'A versão válida já está disponível para impressão.',
            life: 3000,
          });
          this.refreshSelectedQr();
        },
        error: () => this.cdr.markForCheck(),
      });
  }

  private selectActiveQr(
    qrCodes: PontoCriticoQrCode[] | null | undefined,
  ): PontoCriticoQrCode | null {
    return (qrCodes ?? [])
      .filter((qrCode) => qrCode.ativo)
      .sort(
        (a, b) =>
          new Date(b.dataCriacao).getTime() -
          new Date(a.dataCriacao).getTime(),
      )[0] ?? null;
  }

  private revokeQrPreview(): void {
    if (!this.qrPreviewUrl) return;
    URL.revokeObjectURL(this.qrPreviewUrl);
    this.qrPreviewUrl = null;
  }

  setRondaPorTurno(value: number): void {
    this.pontoForm.controls['rondaPorTurno'].setValue(value);
    this.pontoForm.controls['rondaPorTurno'].markAsDirty();
  }

  get periodicidadeLabel(): string {
    const value = Number(this.pontoForm?.get('rondaPorTurno')?.value ?? 1);
    return `${value} ${value === 1 ? 'verificação' : 'verificações'} por turno`;
  }

  get totalObrigatoriosNaPagina(): number {
    return this.pontosCriticos.filter((p) => p.obrigatorio !== false).length;
  }

  get formInvalid(): boolean {
    return this.pontoForm.invalid || this.loadingSavePonto;
  }

  trackById(_: number, item: PontoCritico): number | undefined {
    return item.id;
  }
}